
import React, { createContext, useContext, useState, useEffect } from 'react';
import { FastingProtocol } from '@/types/fasting';

export interface FastingSession {
  id: string;
  startTime: number;
  endTime: number;
  duration: number; // in seconds
  targetDuration: number; // in seconds
  completed: boolean;
  protocolId: string;
  protocolName: string;
  date: string;
}

interface FastingHistoryContextType {
  sessions: FastingSession[];
  addSession: (session: FastingSession) => void;
  clearHistory: () => void;
  getTotalFasts: () => number;
  getCompletedFasts: () => number;
  getLongestFast: () => number;
  getCurrentStreak: () => number;
  getRecentSessions: (count?: number) => FastingSession[];
}

const FastingHistoryContext = createContext<FastingHistoryContextType | undefined>(undefined);

export const FastingHistoryProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [sessions, setSessions] = useState<FastingSession[]>([]);

  // Load sessions from localStorage on mount
  useEffect(() => {
    const savedSessions = localStorage.getItem('fastingSessions');
    if (savedSessions) {
      setSessions(JSON.parse(savedSessions));
    }
  }, []);

  // Save sessions to localStorage when updated
  useEffect(() => {
    localStorage.setItem('fastingSessions', JSON.stringify(sessions));
  }, [sessions]);

  const addSession = (session: FastingSession) => {
    setSessions(prevSessions => [session, ...prevSessions]);
  };

  const clearHistory = () => {
    setSessions([]);
    localStorage.removeItem('fastingSessions');
  };

  const getTotalFasts = () => {
    return sessions.length;
  };

  const getCompletedFasts = () => {
    return sessions.filter(session => session.completed).length;
  };

  const getLongestFast = () => {
    if (sessions.length === 0) return 0;
    
    const longestDuration = Math.max(...sessions.map(s => s.duration));
    // Convert seconds to hours
    return Math.round(longestDuration / 3600);
  };

  const getCurrentStreak = () => {
    if (sessions.length === 0) return 0;
    
    // Sort sessions by date in descending order (newest first)
    const sortedSessions = [...sessions]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    let streak = 0;
    let lastDate: Date | null = null;
    
    for (const session of sortedSessions) {
      if (!session.completed) break;
      
      const sessionDate = new Date(session.date);
      
      if (lastDate === null) {
        streak = 1;
        lastDate = sessionDate;
        continue;
      }
      
      // Check if this session is from the previous day
      const dayDiff = Math.floor((lastDate.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (dayDiff === 1) {
        streak++;
        lastDate = sessionDate;
      } else if (dayDiff === 0) {
        // Same day, continue checking
        continue;
      } else {
        // The streak is broken
        break;
      }
    }
    
    return streak;
  };

  const getRecentSessions = (count = 10) => {
    return sessions.slice(0, count);
  };

  return (
    <FastingHistoryContext.Provider value={{
      sessions,
      addSession,
      clearHistory,
      getTotalFasts,
      getCompletedFasts,
      getLongestFast,
      getCurrentStreak,
      getRecentSessions,
    }}>
      {children}
    </FastingHistoryContext.Provider>
  );
};

export const useFastingHistory = () => {
  const context = useContext(FastingHistoryContext);
  if (context === undefined) {
    throw new Error('useFastingHistory must be used within a FastingHistoryProvider');
  }
  return context;
};
